import React, { useEffect, useState } from "react";
import { Button, DatePicker, Form, Input, Modal, Segmented, Switch, Typography } from "antd";
import { FilePdfOutlined, MailOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { openBlankTab, openFile } from "../../../native/files";
import { emailReport, reportPdf } from "./api";

/**
 * The diary of one project for a period as a PDF: open it, or email it to the client (a copy
 * of what was sent is kept in the files). Days without an entry are listed in the PDF.
 */
const ReportDialog = ({ open, onClose, project, defaultEmail, canEmail }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [mode, setMode] = useState("pdf");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("pdf");
    form.setFieldsValue({
      range: [dayjs().subtract(6, "day"), dayjs()],
      lng: i18n.language === "en" ? "en" : "de",
      photos: true,
      email: defaultEmail || "",
      message: ""
    });
  }, [open]);

  const params = values => ({
    project,
    from: values.range[0].format("YYYY-MM-DD"),
    to: values.range[1].format("YYYY-MM-DD"),
    lng: values.lng,
    photos: values.photos ? "true" : "false"
  });

  const submit = async () => {
    const values = await form.validateFields();
    setBusy(true);
    // Opened before the request: browsers block popups after an await
    const tab = mode === "pdf" ? openBlankTab() : null;
    try {
      if (mode === "pdf") {
        const { blob, fileName } = await reportPdf(params(values));
        await openFile(blob, { tab, fileName });
      } else {
        await emailReport({
          ...params(values),
          photos: values.photos,
          email: values.email,
          message: values.message
        });
        Success(t("The site diary was sent."));
      }
      onClose();
    } catch (error) {
      tab?.close();
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="site-diary-page"
      open={open}
      onCancel={onClose}
      title={t("Site diary as PDF")}
      width={560}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button
          key="go"
          type="primary"
          loading={busy}
          icon={mode === "pdf" ? <FilePdfOutlined /> : <MailOutlined />}
          onClick={submit}
        >
          {mode === "pdf" ? t("Open PDF") : t("Send")}
        </Button>
      ]}
    >
      {canEmail && (
        <Segmented
          block
          value={mode}
          onChange={setMode}
          options={[
            { value: "pdf", label: t("PDF") },
            { value: "email", label: t("Email to the client") }
          ]}
          className="sdi-segmented"
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="range"
          label={t("Period")}
          rules={[{ required: true, message: t("Please choose a period.") }]}
        >
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            className="sdi-full"
            inputReadOnly
            disabledDate={date => date.isAfter(dayjs(), "day")}
          />
        </Form.Item>
        <Form.Item name="lng" label={t("Language of the PDF")}>
          <Segmented
            options={[
              { value: "de", label: "Deutsch" },
              { value: "en", label: "English" }
            ]}
          />
        </Form.Item>
        <Form.Item name="photos" label={t("Include photos")} valuePropName="checked">
          <Switch />
        </Form.Item>
        {mode === "email" && (
          <>
            <Form.Item
              name="email"
              label={t("Email address")}
              rules={[
                { required: true, type: "email", message: t("Please enter an email address.") }
              ]}
            >
              <Input type="email" inputMode="email" />
            </Form.Item>
            <Form.Item name="message" label={t("Message (optional)")}>
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={5000} />
            </Form.Item>
            <Typography.Paragraph type="secondary" className="sdi-small">
              {t("A copy of the sent PDF is kept in your files.")}
            </Typography.Paragraph>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ReportDialog;
